import React from "react";
import { Menu, LogOut, ShieldCheck, KeyRound, ChevronDown, User } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { PortalSwitcher } from "./PortalSwitcher";
import { QuickSearch } from "./QuickSearch";
import { useAuth } from "../hooks/useAuth";
import { useLocalization } from "../hooks/useLocalization";
import { usePermissions } from "../hooks/usePermissions";
import { buildSidebarSections } from "../lib/sidebarConfig";

function pageTitleFromPath(sections, pathname) {
  for (const section of sections) {
    const match = section.items.find((i) => (i.end ? pathname === i.to : pathname.startsWith(i.to)));
    if (match) return match.label;
  }
  return "Management Dashboard";
}

export const AppHeader = ({ title, subtitle, className = "", onOpenMobileNav }) => {
  const { user, logout } = useAuth();
  const { t } = useLocalization();
  const perm = usePermissions();
  const location = useLocation();
  const sections = buildSidebarSections(perm, t);
  const resolvedTitle = title === "Management Dashboard" ? pageTitleFromPath(sections, location.pathname) : title;

  return (
    <header className={`sticky top-0 z-20 backdrop-blur-xl bg-white/95 border-b border-slate-200 px-4 md:px-6 py-3.5 flex items-center justify-between ${className}`}>
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          data-testid="mobile-nav-open-button"
          onClick={onOpenMobileNav}
          className="md:hidden -ml-1 p-2 rounded-lg text-slate-500 hover:bg-slate-100 shrink-0"
          aria-label={t("common.openNavigationMenu")}
        >
          <Menu size={20} />
        </button>
        <div className="min-w-0">
          <h1 className="text-lg md:text-xl font-bold tracking-tight text-slate-900 truncate" data-testid="page-title">{resolvedTitle}</h1>
          {subtitle && <p className="text-sm text-slate-500 truncate">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 md:gap-4 shrink-0">
        <QuickSearch />
        <PortalSwitcher />
        <LanguageSwitcher />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="account-menu-trigger"
              aria-label={t("common.openAccountMenu")}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors active:scale-95"
            >
              <span className="hidden sm:flex flex-col items-end leading-tight">
                <span className="text-sm font-semibold text-slate-800" data-testid="current-user-name">{user?.name}</span>
                <span className="text-[11px] text-slate-500">{user?.role}</span>
              </span>
              <span className="flex items-center justify-center h-8 w-8 rounded-full bg-slate-900 text-white text-xs font-bold">
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </span>
              <ChevronDown size={14} className="text-slate-500 hidden sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" data-testid="account-menu-content" className="w-56">
            <DropdownMenuLabel>
              <span className="block text-sm font-semibold text-slate-900 truncate">{user?.name}</span>
              <span className="block text-xs text-slate-500 truncate">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild data-testid="account-menu-profile">
              <Link to="/management/account" className="flex items-center gap-2 cursor-pointer">
                <User size={14} /> My Account
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild data-testid="account-menu-security">
              <Link to="/management/security" className="flex items-center gap-2 cursor-pointer">
                <ShieldCheck size={14} /> {t("mfa.securityTitle")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild data-testid="account-menu-change-password">
              <Link to="/management/account" className="flex items-center gap-2 cursor-pointer">
                <KeyRound size={14} /> Change Password
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-button" onClick={logout} className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600">
              <LogOut size={14} /> {t("auth.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
